import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, MinimizedModalStrip, SyutSelect } from 'components/ui';
import type { ActionButton, ViewModeOption } from 'components/modals/common/ModuleHeader';
import notificationService from '../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { moduleService } from 'api/services';
import type {
  ScreenDto, CreateScreenRequest, UpdateScreenRequest,
  ScreenElementDto, CreateScreenElementRequest, UpdateScreenElementRequest,
  ScreenElementType,
} from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect/SyutSelectUnified';

// ============================================================================
// ELEMENT FORM MODAL (used inside ManageElementsModal)
// ============================================================================

interface ElementFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  record: ScreenElementDto | null;
  screenId: string;
  onClose: () => void;
  onSave: () => void;
}

const ELEMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'BUTTON', label: 'Button' },
  { value: 'FIELD', label: 'Field' },
  { value: 'COLUMN', label: 'Column' },
  { value: 'TAB', label: 'Tab' },
  { value: 'SECTION', label: 'Section' },
  { value: 'LINK', label: 'Link' },
];

const ElementFormModal: React.FC<ElementFormModalProps> = ({ open, mode, record, screenId, onClose, onSave }) => {
  const [formData, setFormData] = useState({ elementCode: '', elementName: '', elementType: 'BUTTON' as ScreenElementType, description: '', sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && record) {
      setFormData({ elementCode: record.elementCode, elementName: record.elementName, elementType: record.elementType, description: record.description || '', sortOrder: record.sortOrder });
    } else {
      setFormData({ elementCode: '', elementName: '', elementType: 'BUTTON', description: '', sortOrder: 0 });
    }
    setFieldErrors({});
  }, [open, mode, record]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.elementCode.trim()) errors.elementCode = 'Element Code is required';
    if (!formData.elementName.trim()) errors.elementName = 'Element Name is required';
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      if (mode === 'edit' && record) {
        const req: UpdateScreenElementRequest = { screenId, elementCode: formData.elementCode.toUpperCase(), elementName: formData.elementName, elementType: formData.elementType, description: formData.description || undefined, sortOrder: formData.sortOrder, isActive: true, rowVersion: record.rowVersion };
        await moduleService.updateElement(screenId, record.id, req);
        notificationService.toast.success(`Element "${formData.elementName}" has been updated successfully.`);
      } else {
        const req: CreateScreenElementRequest = { screenId, elementCode: formData.elementCode.toUpperCase(), elementName: formData.elementName, elementType: formData.elementType, description: formData.description || undefined, sortOrder: formData.sortOrder, isActive: true };
        await moduleService.createElement(screenId, req);
        notificationService.toast.success(`Element "${formData.elementName}" has been created successfully.`);
      }
      onSave();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [formData, mode, record, screenId, onSave]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop" style={{ zIndex: 10100 }} />
      <div className="modal-container modal-sm" style={{ zIndex: 10101 }}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'edit' ? `Edit Element — ${record?.elementCode}` : 'Add Element'}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Element Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.elementCode ? ' form-field-error' : ''}`}
                value={formData.elementCode}
                onChange={e => { setFormData(p => ({ ...p, elementCode: e.target.value.toUpperCase() })); setFieldErrors(prev => { const { elementCode, ...rest } = prev; return rest; }); }}
                placeholder="e.g., BTN_SAVE"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={50}
              />
              {fieldErrors.elementCode && <div className="form-error-message">{fieldErrors.elementCode}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Element Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.elementName ? ' form-field-error' : ''}`}
                value={formData.elementName}
                onChange={e => { setFormData(p => ({ ...p, elementName: e.target.value })); setFieldErrors(prev => { const { elementName, ...rest } = prev; return rest; }); }}
                placeholder="e.g., Save Button"
                maxLength={150}
              />
              {fieldErrors.elementName && <div className="form-error-message">{fieldErrors.elementName}</div>}
            </div>
          </div>
          <div className="form-grid-2">
            <SyutSelect
              label={<>Element Type <span className="text-danger">*</span></>}
              options={ELEMENT_TYPE_OPTIONS}
              value={formData.elementType}
              onChange={val => setFormData(p => ({ ...p, elementType: (val as ScreenElementType) || 'BUTTON' }))}
              menuPortalTarget={document.body}
            />
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input type="number" className="form-input" value={formData.sortOrder} onChange={e => setFormData(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input type="text" className="form-input" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Element description" maxLength={500} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={mode === 'edit' ? `Save changes to element "${formData.elementName}"?` : `Create element "${formData.elementName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </>
  );
};

// ============================================================================
// MANAGE ELEMENTS MODAL (shows elements for a screen)
// ============================================================================

interface ManageElementsModalProps {
  open: boolean;
  screen: ScreenDto | null;
  onClose: () => void;
}

const ManageElementsModal: React.FC<ManageElementsModalProps> = ({ open, screen, onClose }) => {
  const [elements, setElements] = useState<ScreenElementDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [elemFormOpen, setElemFormOpen] = useState(false);
  const [elemFormMode, setElemFormMode] = useState<'create' | 'edit'>('create');
  const [selectedElem, setSelectedElem] = useState<ScreenElementDto | null>(null);
  const [deleteElem, setDeleteElem] = useState<ScreenElementDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadElements = useCallback(async () => {
    if (!screen) return;
    setLoading(true);
    try {
      const res = await moduleService.getElementsByScreen(screen.id);
      setElements(res.data || []);
    } catch {
      notificationService.toast.error('Failed to load elements');
    } finally {
      setLoading(false);
    }
  }, [screen]);

  useEffect(() => { if (open && screen) loadElements(); }, [open, screen, loadElements]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteElem || !screen) return;
    setIsDeleting(true);
    try {
      await moduleService.deleteElement(screen.id, deleteElem.id);
      notificationService.toast.success(`Element "${deleteElem.elementName}" has been deleted successfully.`);
      setDeleteElem(null);
      loadElements();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to delete element');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteElem, screen, loadElements]);

  if (!open || !screen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Elements — {screen.screenName}</h2>
          <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
            <button className="btn btn-primary btn-sm" onClick={() => { setSelectedElem(null); setElemFormMode('create'); setElemFormOpen(true); }}>
              <IconSystem.add size={14} /> Add Element
            </button>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Loading...</div>
          ) : elements.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>No elements defined for this screen.</div>
          ) : (
            <table className="simple-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.75rem', fontWeight: 600 }}>Code</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.75rem', fontWeight: 600 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.75rem', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.75rem', fontWeight: 600 }}>Order</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.75rem', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {elements.map(el => (
                  <tr key={String(el.id)}>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontFamily: 'monospace', fontWeight: 600, fontSize: '0.8rem' }}>{el.elementCode}</td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem' }}>{el.elementName}</td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem' }}>
                      <span className="badge badge-info">{el.elementType}</span>
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', textAlign: 'center' }}>{el.sortOrder}</td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>
                      <button className="btn-icon" title="Edit" onClick={() => { setSelectedElem(el); setElemFormMode('edit'); setElemFormOpen(true); }}><IconSystem.edit size={14} /></button>
                      <button className="btn-icon" title="Delete" onClick={() => setDeleteElem(el)} style={{ color: 'var(--color-danger)' }}><IconSystem.trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <ElementFormModal
        open={elemFormOpen}
        mode={elemFormMode}
        record={selectedElem}
        screenId={String(screen.id)}
        onClose={() => { setElemFormOpen(false); setSelectedElem(null); }}
        onSave={() => { setElemFormOpen(false); setSelectedElem(null); loadElements(); }}
      />

      <ConfirmDialog
        open={!!deleteElem}
        title="Delete Element"
        message={`Delete element "${deleteElem?.elementName}"? This cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteElem(null)}
        variant="danger"
      />
    </>
  );
};

// ============================================================================
// SCREEN FORM MODAL
// ============================================================================

interface ScreenFormModalProps {
  open: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: ScreenDto | null;
  onClose: () => void;
  onSave: () => void;
  tabId?: string;
  moduleName?: string;
  instanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const ScreenFormModal: React.FC<ScreenFormModalProps> = ({ open, mode, record, onClose, onSave, tabId, moduleName, instanceId, minimizeRef, onRestoreModal }) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState({ screenCode: '', screenName: '', description: '', routePath: '' });
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const savedSnapshot = useRef<string | null>(null);

  // Refs for minimize/restore — captures current state in closure
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isRestoreRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (mode === 'edit' && record) {
      const fd = { screenCode: record.screenCode, screenName: record.screenName, description: record.description || '', routePath: record.routePath || '' };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else if (mode === 'copy' && record) {
      const fd = { screenCode: '', screenName: record.screenName + ' (Copy)', description: record.description || '', routePath: record.routePath || '' };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else {
      const fd = { screenCode: '', screenName: '', description: '', routePath: '' };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    }
    setFieldErrors({});
  }, [open, mode, record]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentFormData = { ...formDataRef.current };
    const currentSnapshot = savedSnapshot.current || '';
    const currentIsDirty = currentSnapshot ? JSON.stringify(currentFormData) !== currentSnapshot : false;
    const idx = instanceId || '1';
    let title: string;
    if (mode === 'create' || mode === 'copy') {
      const prefix = mode === 'copy' ? 'Copy' : 'Add';
      title = currentFormData.screenCode ? `${prefix} - ${currentFormData.screenCode} (${idx})` : currentFormData.screenName ? `${prefix} - ${currentFormData.screenName} (${idx})` : `${prefix} New (${idx})`;
    } else {
      title = `Edit - ${record?.screenCode || currentFormData.screenCode}`;
    }
    minimize({
      id: tabId ? `${tabId}-screen-modal-${mode === 'edit' ? record?.id : mode + '-' + instanceId}` : 'screen-modal',
      tabId: tabId!,
      moduleName: moduleName || 'Screen Master',
      title,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(currentFormData);
        savedSnapshot.current = currentSnapshot;
        onRestoreModal?.(mode, instanceId, mode !== 'create' ? record?.screenCode : undefined);
      },
    });
    onClose();
  }, [tabId, instanceId, mode, record, minimize, moduleName, onRestoreModal, onClose]);

  useEffect(() => { if (minimizeRef) minimizeRef.current = handleMinimize; }, [minimizeRef, handleMinimize]);

  const handleClose = useCallback(() => {
    const isDirty = savedSnapshot.current ? JSON.stringify(formData) !== savedSnapshot.current : false;
    if (isDirty) {
      setCloseConfirmOpen(true);
    } else {
      onClose();
    }
  }, [formData, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.screenCode.trim()) errors.screenCode = 'Screen Code is required';
    if (!formData.screenName.trim()) errors.screenName = 'Screen Name is required';
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      if (mode === 'edit' && record) {
        const req: UpdateScreenRequest = { screenCode: formData.screenCode.toUpperCase(), screenName: formData.screenName, description: formData.description || undefined, routePath: formData.routePath || undefined, isActive: true, rowVersion: record.rowVersion };
        await moduleService.updateScreen(record.id, req);
        notificationService.toast.success(`Screen "${formData.screenName}" has been updated successfully.`);
      } else {
        const req: CreateScreenRequest = { screenCode: formData.screenCode.toUpperCase(), screenName: formData.screenName, description: formData.description || undefined, routePath: formData.routePath || undefined, isActive: true };
        await moduleService.createScreen(req);
        notificationService.toast.success(`Screen "${formData.screenName}" has been created successfully.`);
      }
      onSave();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'An error occurred while saving. Please try again.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [formData, mode, record, onSave]);

  if (!open) return null;
  const title = mode === 'create' ? 'Add Screen' : mode === 'copy' ? 'Copy Screen' : `Edit Screen — ${record?.screenCode}`;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Screen Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.screenCode ? ' form-field-error' : ''}`}
                value={formData.screenCode}
                onChange={e => { setFormData(p => ({ ...p, screenCode: e.target.value.toUpperCase() })); setFieldErrors(prev => { const { screenCode, ...rest } = prev; return rest; }); }}
                placeholder="e.g., SCR_ROLE_MASTER"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={50}
              />
              {fieldErrors.screenCode && <div className="form-error-message">{fieldErrors.screenCode}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Screen Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.screenName ? ' form-field-error' : ''}`}
                value={formData.screenName}
                onChange={e => { setFormData(p => ({ ...p, screenName: e.target.value })); setFieldErrors(prev => { const { screenName, ...rest } = prev; return rest; }); }}
                placeholder="e.g., Role Master"
                maxLength={150}
              />
              {fieldErrors.screenName && <div className="form-error-message">{fieldErrors.screenName}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} rows={2} maxLength={500} placeholder="Screen description" />
          </div>
          <div className="form-group">
            <label className="form-label">Route Path</label>
            <input type="text" className="form-input" value={formData.routePath} onChange={e => setFormData(p => ({ ...p, routePath: e.target.value }))} maxLength={200} placeholder="e.g. /iam/roles" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Screen'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={mode === 'edit' ? `Save changes to screen "${formData.screenName}"?` : `Create screen "${formData.screenName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close? Your changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={() => { setCloseConfirmOpen(false); onClose(); }}
        onCancel={() => setCloseConfirmOpen(false)}
        variant="danger"
      />
    </>
  );
};

// ============================================================================
// SCREEN MASTER SCREEN
// ============================================================================

interface ScreenMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

type GridRecord = Omit<ScreenDto, 'id'> & { id: string };

const ScreenMasterScreen: React.FC<ScreenMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized } = useModalMinimize();

  // View & filter state
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [filterOpen, setFilterOpen] = useState(false);

  // Grid state
  const [queryParams, setQueryParams] = useState<{ page: number; pageSize: number; searchTerm?: string }>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<ScreenDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<ScreenDto | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [elemsScreen, setElemsScreen] = useState<ScreenDto | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Load data — silent=true skips loading spinner
  const loadData = useCallback(async (params: typeof queryParams, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await moduleService.getAllScreens(params);
      setData(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch {
      notificationService.toast.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(queryParams); }, [queryParams, loadData]);

  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const refetch = useCallback(() => loadData(queryParams, true), [loadData, queryParams]);

  const handleSaveSuccess = useCallback(() => { setFormModalOpen(false); setSelectedRecord(null); refetch(); }, [refetch]);

  // CRUD handlers
  const handleAdd = useCallback(() => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    createInstanceCounter.current += 1;
    setCreateInstanceId(String(createInstanceCounter.current));
    setSelectedRecord(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback((scr: ScreenDto) => {
    const modalId = `${tabId}-screen-modal-${scr.id}`;
    if (tabId && isMinimized(modalId)) { dismissMinimized(modalId); setSelectedRecord(scr); setFormMode('edit'); setFormModalOpen(true); return; }
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    setSelectedRecord(scr);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized, dismissMinimized]);

  const handleCopy = useCallback((scr: ScreenDto) => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    copyInstanceCounter.current += 1;
    setCopyInstanceId(String(copyInstanceCounter.current));
    setSelectedRecord(scr);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleDeleteRequest = useCallback((scr: ScreenDto) => { setSelectedRecord(scr); setDeleteModalOpen(true); }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRecord) return;
    setIsDeleting(true);
    try {
      await moduleService.deleteScreen(selectedRecord.id);
      notificationService.toast.success(`Screen "${selectedRecord.screenName}" has been deleted successfully.`);
      setDeleteModalOpen(false);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to delete screen');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRecord, refetch]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instId?: string, code?: string) => {
    if (instId) { if (mode === 'copy') setCopyInstanceId(instId); else setCreateInstanceId(instId); }
    if (code) { const rec = data.find(r => r.screenCode === code); if (rec) setSelectedRecord(rec); }
    setFormMode(mode);
    setFormModalOpen(true);
  }, [data]);

  // Grid columns
  const columns: SyutGridColumn<GridRecord>[] = useMemo(() => [
    { id: 'screenCode', header: 'Code', accessorKey: 'screenCode', size: 140, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.original.screenCode}</span> },
    { id: 'screenName', header: 'Screen Name', accessorKey: 'screenName', size: 220 },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 250 },
    { id: 'routePath', header: 'Route', accessorKey: 'routePath', size: 180, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{row.original.routePath || '—'}</span> },
    { id: 'elementCount', header: 'Elements', accessorKey: 'elementCount', size: 80, cell: ({ row }: any) => <span style={{ textAlign: 'right', display: 'block' }}>{row.original.elementCount ?? 0}</span> },
    {
      id: 'actions', header: 'Actions', size: 70,
      cell: ({ row }: any) => {
        const s = row.original as ScreenDto;
        const items: ActionMenuItem[] = [
          { id: 'elements', label: 'Manage Elements', icon: <IconSystem.grid size={14} />, onClick: () => setElemsScreen(s) },
          { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => handleEdit(s) },
          { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={14} />, onClick: () => handleCopy(s) },
          { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={14} />, onClick: () => handleDeleteRequest(s), variant: 'danger' },
        ];
        return <ActionMenu items={items} />;
      },
    },
  ], [handleEdit, handleCopy, handleDeleteRequest]);

  const gridData = data as unknown as GridRecord[];

  // View mode options
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View (Not Applicable)', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // Action buttons
  const actionButtons: ActionButton[] = [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Screen', onClick: handleAdd, tooltip: 'Add New Screen', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => {}, tooltip: 'Export', variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Screen Master"
        description="Manage application screens and their elements"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search screens...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as 'list' | 'grid') }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={gridData}
            columns={columns}
            isLoading={loading}
            manualPagination
            totalCount={totalCount}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={idx => setQueryParams(p => ({ ...p, page: idx + 1 }))}
            onPageSizeChange={sz => setQueryParams(p => ({ ...p, pageSize: sz, page: 1 }))}
            emptyMessage="No screens found"
          />
        </div>
      </div>

      <ScreenFormModal
        open={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        onSave={handleSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        instanceId={formMode === 'copy' ? copyInstanceId : createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal}
      />

      <ManageElementsModal
        open={!!elemsScreen}
        screen={elemsScreen}
        onClose={() => setElemsScreen(null)}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Screen"
        message={`Delete screen "${selectedRecord?.screenName}"? All associated elements will also be removed. This cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRecord(null); }}
        variant="danger"
      />
    </div>
  );
};

export default ScreenMasterScreen;
